import { z } from 'zod';
// Types for GET /api/v1/scans/all.
// Schema verified against the live dev API on 2026-06-02.
//
// The device payload is noisy and partially optional, so the schema is precise
// where we render (`rawData` region readings) and loose where we don't:
//  - `environment` / `device` are nullable (absent on scans with no readings)
//  - `metaJSON` is `z.unknown()` — a large per-region sensor blob shown raw,
//    never parsed. Unknown extra fields are stripped by Zod's default behavior.
// A single per-region reading inside `rawData`.
export const ScanReadingSchema = z.object({
    type: z.string(), // e.g. 'left_cheek' | 'right_cheek' | 'forehead' — kept loose
    hydration: z.number(),
    elasticity: z.number(),
    oiliness: z.number(),
});
export const ScanEnvironmentSchema = z.object({
    humid: z.number(),
    temp: z.number(),
    tempUnit: z.string(),
});
export const ScanDeviceSchema = z.object({
    serial: z.string(),
    hwVer: z.string(),
    fwVer: z.string(),
});
export const ScanSchema = z.object({
    _id: z.string(),
    user: z.string(),
    // Both are `null` on some real scans (e.g. incomplete/legacy records), so they
    // must be nullable or the whole page fails to parse. Rendered defensively.
    takenAt: z.string().nullable(), // ISO datetime
    photoUrl: z.string(),
    rawData: z.array(ScanReadingSchema),
    environment: ScanEnvironmentSchema.nullable(),
    scanWindow: z.string().nullable(), // 'morning' | 'evening' — kept loose
    device: ScanDeviceSchema.nullable(),
    // Raw per-region sensor blob; displayed, never parsed. The key is absent on
    // many scans, and in Zod v4 `z.unknown()` is NOT implicitly optional (unlike
    // v3), so `.optional()` is required or the whole page fails to parse.
    metaJSON: z.unknown().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
});
export const ScansMetaSchema = z.object({
    totalDocs: z.number(),
    totalPages: z.number(),
    page: z.number(),
    limit: z.number(),
    userId: z.string().nullable().optional(),
});
// Standard API envelope: { success, errorCode, message, data, meta }.
export const ScansEnvelopeSchema = z.object({
    success: z.boolean(),
    errorCode: z.string(),
    message: z.string(),
    data: z.array(ScanSchema),
    meta: ScansMetaSchema,
});
